import React, { useCallback, useEffect, useMemo, useState } from 'react';
import wordmarkBlack from '../Assets/mf-wordmark-black.png';
import wordmarkWhite from '../Assets/mf-wordmark-white.png';
import DocsCodeBlock from '../Components/Docs/DocsCodeBlock';
import DocsCodePreview from '../Components/Docs/DocsCodePreview';
import DocsFigure from '../Components/Docs/DocsFigure';
import {
  APP_VERSION,
  MF_PRIVACY_URL,
  MF_SUPPORT_URL,
  MF_TERMS_URL,
} from '../utils/legalLinks';
import './IntegrationGuideDocs.css';

// Visuals: export from Figma into src/Assets/docs/ when adding new figures.
import heroDevices from '../Assets/docs/hero-devices.svg';
import embedAnatomy from '../Assets/docs/embed-anatomy.svg';
import integrationJourney from '../Assets/docs/integration-journey.svg';

// Staging handoff. Production docs stay on the previous guide until this is promoted.
const SERHANT_ORIGIN = 'https://staging-serhant.magicfurnish.com';
const EMBED_BASE = `${SERHANT_ORIGIN}/external-ai-upload`;
const DOCS_SUPPORT_EMAIL = 'support@magicfurnish.com';
const DOCS_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=GFS+Didot&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@300;400;500;600&display=swap';

const EMBED_IFRAME_TEXT = `<iframe
  id="magicfurnish-creator"
  title="Create Magic Floor Plan"
  width="100%"
  height="240"
  style="max-width: 360px; background: transparent; border: none; overflow: hidden;">
</iframe>`;

const IOS_WEBVIEW_TEXT = `webView.isOpaque = false
webView.backgroundColor = .clear`;

const LISTEN_HANDLER_TEXT = `const iframe = document.getElementById('magicfurnish-creator');
const MAGICFURNISH_ORIGIN = '${SERHANT_ORIGIN}';
// S.MPLE listing record ID that launched this iframe — associate the result with THIS id.
const launchedListingRecordId = 'YOUR_LISTING_RECORD_ID';

async function onMagicFurnishMessage(event) {
  if (event.origin !== MAGICFURNISH_ORIGIN) return;
  if (!iframe || event.source !== iframe.contentWindow) return;
  if (event.data?.type !== 'mf:complete') return;

  const { galleryUrl, ref: echoedRef } = event.data;
  if (typeof galleryUrl !== 'string' || !galleryUrl) return;

  let parsedGalleryUrl;
  try {
    parsedGalleryUrl = new URL(galleryUrl);
  } catch {
    return;
  }
  if (parsedGalleryUrl.origin !== MAGICFURNISH_ORIGIN) return;

  // Consistency check only — do not trust echoedRef alone.
  if (
    typeof echoedRef === 'string' &&
    echoedRef.length > 0 &&
    echoedRef !== launchedListingRecordId
  ) {
    console.warn('mf:complete ref mismatch', {
      echoedRef,
      launchedListingRecordId,
    });
    return;
  }

  // Store galleryUrl privately on the S.MPLE listing. This does not put it on
  // serhant.com. Idempotent for the same launchedListingRecordId + galleryUrl
  // pair. A newer distinct galleryUrl replaces the previously stored one.
  try {
    await persistGalleryUrlForListing(launchedListingRecordId, galleryUrl);
    dismissOrReplaceUploader();
    window.removeEventListener('message', onMagicFurnishMessage);
  } catch (error) {
    // If persistence fails, do not dismiss the uploader or remove the listener.
    // Retain the returned galleryUrl and retry the host-side save without another upload.
    console.error('Failed to persist Magic Floor Plan URL', error);
  }
}

// Register the listener BEFORE assigning iframe.src.
window.addEventListener('message', onMagicFurnishMessage);
iframe.src =
  '${EMBED_BASE}?host_app=smple&ref=' +
  encodeURIComponent(launchedListingRecordId);

// Call from the host teardown, unmount, or uploader-close path.
function onUploaderTornDown() {
  window.removeEventListener('message', onMagicFurnishMessage);
  iframe.removeAttribute('src');
}`;

const TEST_HARNESS_TEXT = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>MagicFurnish embed test</title>
  <style>
    body { font-family: sans-serif; padding: 20px; max-width: 480px; }
    #frame-wrap { max-width: 360px; width: 100%; }
    #output { margin-top: 16px; padding: 12px; background: #f4f4f4; white-space: pre-wrap; min-height: 80px; }
  </style>
</head>
<body>
  <h1>Embed test</h1>
  <div id="frame-wrap">
    <iframe
      id="magicfurnish-creator"
      title="Create Magic Floor Plan"
      width="360"
      height="240"
      style="display: block; width: 100%; max-width: 360px; height: 240px; background: transparent; border: none; overflow: hidden;">
    </iframe>
  </div>
  <h2>mf:complete output</h2>
  <pre id="output">Waiting for mf:complete…</pre>
  <script>
    const iframe = document.getElementById('magicfurnish-creator');
    const MAGICFURNISH_ORIGIN = '${SERHANT_ORIGIN}';
    function onMessage(event) {
      if (event.origin !== MAGICFURNISH_ORIGIN) return;
      if (event.source !== iframe.contentWindow) return;
      if (event.data?.type !== 'mf:complete') return;
      document.getElementById('output').textContent =
        JSON.stringify(event.data, null, 2);
    }
    window.addEventListener('message', onMessage);
    iframe.src =
      '${EMBED_BASE}?host_app=smple&ref=' + encodeURIComponent('TEST123');
  </script>
</body>
</html>`;

const MF_COMPLETE_PAYLOAD_TEXT = `{
  "type": "mf:complete",
  "galleryUrl": "https://staging-serhant.magicfurnish.com/floor-gallery/507f1f77bcf86cd799439011",
  "uniqueId": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "ref": "YOUR_LISTING_RECORD_ID"
}`;

const DOC_NAV_GROUPS = [
  {
    heading: 'Start here',
    primary: true,
    items: [
      { id: 'quickstart', label: 'Quickstart' },
      { id: 'embed', label: 'Embed' },
      { id: 'listen', label: 'Persist' },
      { id: 'display', label: 'Display in S.MPLE' },
      { id: 'checklist', label: 'Embed checklist' },
    ],
  },
  {
    heading: 'Contract reference',
    items: [
      { id: 'parameters', label: 'Parameters & payload' },
      { id: 'galleries', label: 'Re-uploads & failures' },
    ],
  },
  {
    heading: 'Security & operations',
    items: [
      { id: 'security', label: 'Security' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    heading: 'Additional resources',
    items: [
      { id: 'platform', label: 'iOS & smoke test' },
      { id: 'analytics', label: 'Analytics' },
    ],
  },
  {
    heading: 'Production launch',
    items: [
      { id: 'publishing', label: 'Publishing on serhant.com' },
      { id: 'kickoff', label: 'Launch inputs' },
    ],
  },
];

const MOBILE_NAV_MQ = '(max-width: 900px)';
const SCROLL_SPY_OFFSET = 80;

const THEME_STORAGE_KEY = 'mf-docs-theme';
const THEMES = ['light', 'dark'];

// Remember an explicit choice; otherwise seed from the OS preference on first
// visit so the initial paint still matches the user's system.
const readInitialTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (THEMES.includes(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    /* matchMedia unavailable */
  }
  return 'light';
};

const IntegrationGuideDocs = () => {
  const navSectionIds = useMemo(
    () => DOC_NAV_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
    [],
  );
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [theme, setTheme] = useState(readInitialTheme);
  const [smokeExpanded, setSmokeExpanded] = useState(false);
  const [listenerExpanded, setListenerExpanded] = useState(false);

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* storage unavailable */
    }
  }, [theme]);

  const scrollToTop = useCallback(() => {
    closeNav();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [closeNav]);

  useEffect(() => {
    document.documentElement.classList.add('mf-docs-page');
    return () => {
      document.documentElement.classList.remove('mf-docs-page');
    };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'MagicFurnish Integration Guide';

    const existing = document.querySelector('link[data-mf-integration-guide-font]');
    let link = existing;
    let addedLink = false;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = DOCS_FONTS_HREF;
      link.setAttribute('data-mf-integration-guide-font', 'true');
      document.head.appendChild(link);
      addedLink = true;
    }

    return () => {
      document.title = previousTitle;
      if (addedLink && link?.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, []);

  useEffect(() => {
    if (!navOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeNav();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen, closeNav]);

  useEffect(() => {
    if (!navOpen) return undefined;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previous = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      htmlOverflow: documentElement.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.overflow = previous.bodyOverflow;
      documentElement.style.overflow = previous.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [navOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_NAV_MQ);
    const onChange = () => {
      if (!mediaQuery.matches) closeNav();
    };

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [closeNav]);

  useEffect(() => {
    const main = document.querySelector('.mf-integration-guide .docs-main');
    if (!main) return undefined;

    const usedIds = new Set(
      Array.from(document.querySelectorAll('[id]'), (el) => el.id),
    );

    const slugify = (value) =>
      (value || '')
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    const headings = Array.from(main.querySelectorAll('h2, h3'));
    const createdAnchors = [];

    // 1. Deep-linkable headings: ensure an id + a hover-reveal "#" anchor that
    //    copies the section URL to the clipboard.
    headings.forEach((heading) => {
      if (!heading.id) {
        const base = slugify(heading.textContent) || 'section';
        let id = base;
        let n = 2;
        while (usedIds.has(id)) id = `${base}-${n++}`;
        usedIds.add(id);
        heading.id = id;
      }
      heading.classList.add('docs-heading');
      heading.style.scrollMarginTop = `${SCROLL_SPY_OFFSET}px`;

      if (heading.querySelector('.docs-heading-anchor')) return;

      const label = (heading.textContent || 'section').trim();
      const anchor = document.createElement('a');
      anchor.className = 'docs-heading-anchor';
      anchor.href = `#${heading.id}`;
      anchor.setAttribute('aria-label', `Copy link to “${label}”`);
      anchor.innerHTML = '<span aria-hidden="true">#</span>';

      const onClick = (event) => {
        event.preventDefault();
        const url = `${window.location.origin}${window.location.pathname}#${heading.id}`;
        window.history.pushState(null, '', `#${heading.id}`);
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (navigator.clipboard?.writeText) {
          navigator.clipboard
            .writeText(url)
            .then(() => {
              anchor.classList.add('is-copied');
              window.setTimeout(() => anchor.classList.remove('is-copied'), 1600);
            })
            .catch(() => {});
        }
      };

      anchor.addEventListener('click', onClick);
      heading.appendChild(anchor);
      createdAnchors.push({ anchor, onClick });
    });

    // 2. Scroll spy: highlight the nav item for the section at the reading line
    //    (under the sticky topbar). Only nav section roots are candidates so the
    //    active item maps 1:1 to a sidebar link. Nested nav targets (e.g.
    //    #reconcile inside #galleries) win once their top crosses the line.
    const candidates = navSectionIds
      .map((id) => {
        const el = document.getElementById(id);
        return el ? { el, navId: id } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        const pos = a.el.compareDocumentPosition(b.el);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

    let frame = 0;
    const pickActive = () => {
      let current = null;
      candidates.forEach(({ el, navId }) => {
        if (el.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
          current = navId;
        }
      });
      setActiveSection((prev) => (prev === current ? prev : current));
    };
    const schedulePick = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        pickActive();
      });
    };

    if (candidates.length) {
      window.addEventListener('scroll', schedulePick, { passive: true });
      window.addEventListener('resize', schedulePick);
      window.addEventListener('hashchange', pickActive);
      pickActive();
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedulePick);
      window.removeEventListener('resize', schedulePick);
      window.removeEventListener('hashchange', pickActive);
      createdAnchors.forEach(({ anchor, onClick }) => {
        anchor.removeEventListener('click', onClick);
        anchor.remove();
      });
    };
  }, [navSectionIds]);

  const wordmarkSrc = theme === 'dark' ? wordmarkWhite : wordmarkBlack;

  return (
    <div
      className={`mf-integration-guide${navOpen ? ' docs-nav-open' : ''}`}
      data-mf-resolved={theme}
    >
      <header className="docs-topbar">
        <div className="docs-topbar-inner">
          <div className="docs-topbar-start">
            <button type="button" className="docs-topbar-brand" onClick={scrollToTop}>
              <img
                src={wordmarkSrc}
                alt="MagicFurnish"
                className="docs-topbar-wordmark-img"
              />
            </button>
          </div>
          <div className="docs-topbar-end">
            <div className="docs-theme-switch" role="group" aria-label="Color theme">
              <button
                type="button"
                className="docs-theme-seg"
                data-active={theme === 'light'}
                aria-pressed={theme === 'light'}
                aria-label="Light mode"
                title="Light mode"
                onClick={() => setTheme('light')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="4.2" />
                  <path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7" />
                </svg>
              </button>
              <button
                type="button"
                className="docs-theme-seg"
                data-active={theme === 'dark'}
                aria-pressed={theme === 'dark'}
                aria-label="Dark mode"
                title="Dark mode"
                onClick={() => setTheme('dark')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 13.2A8.2 8.2 0 1 1 10.8 4a6.4 6.4 0 0 0 9.2 9.2z" />
                </svg>
              </button>
            </div>
            <button
              type="button"
              className={`docs-menu-toggle${navOpen ? ' is-open' : ''}`}
              aria-expanded={navOpen}
              aria-controls="docs-nav-drawer"
              onClick={() => setNavOpen((open) => !open)}
            >
              <span className="docs-menu-icon" aria-hidden="true" />
              <span className="docs-menu-label">{navOpen ? 'Close menu' : 'Open menu'}</span>
            </button>
          </div>
        </div>
      </header>

      <button
        type="button"
        className="docs-nav-backdrop"
        aria-label="Close navigation"
        aria-hidden={!navOpen}
        tabIndex={navOpen ? 0 : -1}
        onClick={closeNav}
      />

      <div className="docs-shell">
        <aside
          className={`docs-sidebar${navOpen ? ' is-open' : ''}`}
          id="docs-nav-drawer"
          aria-label="Documentation sections"
        >
          <nav className="docs-nav">
            {DOC_NAV_GROUPS.map((group) => (
              <div
                key={group.heading}
                className={`docs-nav-group${group.primary ? ' docs-nav-group--primary' : ''}`}
              >
                <p className="docs-nav-group-heading">{group.heading}</p>
                <ul>
                  {group.items.map(({ id, label }) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        className={activeSection === id ? 'is-active' : undefined}
                        aria-current={activeSection === id ? 'location' : undefined}
                        onClick={closeNav}
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <div className="wrap docs-main">
          <header className="docs-hero">
            <p className="docs-hero-eyebrow">S.MPLE Integration Guide</p>
            <h1 className="doc-title">
              One floor plan in.
              <br />
              One interactive URL out.
            </h1>
            <p className="doc-sub">
              MagicFurnish turns a floor plan image into an interactive gallery and returns a{' '}
              <code>galleryUrl</code> for S.MPLE to store and display as Magic Floor Plan. A
              separate Luxury Presence sync is required to publish the links on serhant.com (see{' '}
              <a href="#publishing">Publishing</a>).
            </p>
          </header>

          <DocsFigure
            src={heroDevices}
            frame={false}
            className="docs-hero-figure"
            aspectRatio="16 / 9"
            alt="MagicFurnish floor plan gallery on desktop and mobile."
          />

          <section className="quickstart" id="quickstart">
            <h2 className="quickstart-title">Quickstart</h2>
            <ol className="quickstart-steps">
              <li>
                <a href="#embed"><strong>Embed</strong></a>: create the iframe without{' '}
                <code>src</code>, register the <code>message</code> listener, then assign{' '}
                <code>{EMBED_BASE}?host_app=smple&amp;ref={'{listing_record_id}'}</code> (URL-encode
                the S.MPLE listing record ID as <code>ref</code>).
              </li>
              <li>
                <a href="#listen"><strong>Persist</strong></a>: on <code>mf:complete</code>,
                validate the event, store the returned <code>galleryUrl</code> privately on the
                S.MPLE listing record (replacing any previously stored <code>galleryUrl</code> for
                that listing), then dismiss the uploader. This step does not put the floor plan on
                serhant.com.
              </li>
              <li>
                <a href="#display"><strong>Display in S.MPLE</strong></a>: show Magic Floor Plan
                only when a stored <code>galleryUrl</code> exists; open it in the top-level window.
                After an edit, open the current link from a new lookup.
              </li>
            </ol>
          </section>

          <section className="phase" id="phase-1">
            <div className="step" id="embed">
              <div className="step-head">
                <span className="step-num">01</span>
                <span className="step-name">Embed the uploader</span>
              </div>
              <p>
                Create the iframe without a <code>src</code>. After the listener is registered
                (next step), assign:
              </p>
              <p>
                <code>{EMBED_BASE}?host_app=smple&amp;ref={'{listing_record_id}'}</code>
              </p>
              <p>
                URL-encode the S.MPLE listing record ID as <code>ref</code>. If the host page sets
                CSP, allow <code>{SERHANT_ORIGIN}</code> in <code>frame-src</code>.
              </p>
              <DocsCodeBlock text={EMBED_IFRAME_TEXT} label="iframe">
                &lt;<span className="k">iframe</span>
                {'\n  '}
                <span className="k">id</span>=<span className="s">&quot;magicfurnish-creator&quot;</span>
                {'\n  '}
                <span className="k">title</span>=<span className="s">&quot;Create Magic Floor Plan&quot;</span>
                {'\n  '}
                <span className="k">width</span>=<span className="s">&quot;100%&quot;</span>
                {'\n  '}
                <span className="k">height</span>=<span className="s">&quot;240&quot;</span>
                {'\n  '}
                <span className="k">style</span>=<span className="s">&quot;max-width: 360px; background: transparent; border: none; overflow: hidden;&quot;</span>&gt;
                {'\n'}
                &lt;/<span className="k">iframe</span>&gt;
              </DocsCodeBlock>

              <h3 className="ref-heading">Layout</h3>
              <p>
                Supported at 320–360px wide and 240px tall:{' '}
                <code>width: 100%; max-width: 360px; height: 240px;</code>
              </p>
              <DocsFigure
                src={embedAnatomy}
                aspectRatio="16 / 9"
                alt="Upload card at 320px and 360px wide, both 240px tall."
              />
              <div className="docs-note">
                <strong>Accepted uploads:</strong> one JPG, JPEG, or PNG floor plan, up to
                10&nbsp;MB.
              </div>
            </div>

            <div className="step" id="listen">
              <div className="step-head">
                <span className="step-num">02</span>
                <span className="step-name">Persist mf:complete</span>
              </div>
              <p>
                Attach the listener on the iframe&apos;s immediate parent <strong>before</strong>{' '}
                setting <code>iframe.src</code>. Validate origin, source, and payload.
              </p>
              <ul>
                <li>
                  <code>mf:complete</code> is posted automatically when processing finishes and a{' '}
                  <code>galleryUrl</code> is ready — not when the file upload alone succeeds, and not
                  when the agent clicks <em>View furnished layout</em>.
                </li>
                <li>
                  Store the returned <code>galleryUrl</code> privately against the S.MPLE listing
                  record before dismissing the uploader (treat echoed <code>ref</code> as a
                  consistency check only). For another upload on the same listing, replace that
                  privately stored <code>galleryUrl</code>. Do not put it on serhant.com from this
                  step.
                </li>
                <li>
                  If persistence fails, do not dismiss the uploader or remove the listener. Retain
                  the returned <code>galleryUrl</code> and retry the host-side save without
                  requiring another upload. Remove the listener after a successful persist.
                </li>
                <li>
                  For persistence, ignore any parent message other than{' '}
                  <code>type === &quot;mf:complete&quot;</code>.
                </li>
              </ul>
              <div className="docs-note">
                Validate <code>event.origin</code> exactly — the iframe posts with{' '}
                <code>targetOrigin: &quot;*&quot;</code>.
              </div>
              <DocsCodePreview
                label="message listener"
                expanded={listenerExpanded}
                onToggle={() => setListenerExpanded((open) => !open)}
              >
                <DocsCodeBlock text={LISTEN_HANDLER_TEXT} label="message listener" />
              </DocsCodePreview>
              <div className="docs-note">
                <code>persistGalleryUrlForListing</code> and <code>dismissOrReplaceUploader</code>{' '}
                represent S.MPLE&apos;s host-side persistence and uploader lifecycle logic.
              </div>
            </div>

            <div className="step" id="display">
              <div className="step-head">
                <span className="step-num">03</span>
                <span className="step-name">Display in S.MPLE</span>
              </div>
              <p>
                If the S.MPLE listing record has a stored <code>galleryUrl</code>, show Magic Floor
                Plan inside S.MPLE and open it in the top-level window. Only show it when a stored
                URL exists. When the agent edits, open the <code>galleryUrl</code> from a new
                lookup, not the first <code>mf:complete</code>.
              </p>
              <p>
                This is separate from the in-card <em>View furnished layout</em> control (see{' '}
                <a href="#view-furnished-layout">View furnished layout</a>).
              </p>
              <div className="docs-note">
                For serhant.com, write <code>galleryUrl</code> to Luxury Presence when the agent
                publishes — see <a href="#publishing">Publishing on serhant.com</a>.
              </div>
            </div>

            <div className="step" id="checklist">
              <h3 className="ref-heading">Embed checklist</h3>
              <ul className="checklist">
                <li>
                  Listener registered before <code>iframe.src</code>
                </li>
                <li>
                  Exact <code>event.origin</code> === <code>{SERHANT_ORIGIN}</code>
                </li>
                <li>
                  Exact <code>event.source === iframe.contentWindow</code>
                </li>
                <li>
                  Valid <code>galleryUrl</code> (string, parseable, origin{' '}
                  <code>{SERHANT_ORIGIN}</code>)
                </li>
                <li>Persisted against the launching S.MPLE listing record ID</li>
                <li>
                  Idempotent for the same <code>galleryUrl</code>; replace on a new one
                </li>
                <li>Uploader dismissed only after persistence succeeds</li>
                <li>
                  Magic Floor Plan in S.MPLE only when a stored <code>galleryUrl</code> exists
                </li>
              </ul>
            </div>
          </section>

          <div className="docs-layer-head" role="presentation">
            <span className="docs-layer-kicker">Contract reference</span>
          </div>

          <section className="phase docs-phase--ref" id="parameters">
            <h2 className="phase-title">Parameters &amp; payload</h2>
            <p>Set on <code>iframe.src</code> after the listener is registered.</p>
            <table className="scope-ownership">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Requirement</th>
                  <th>Behavior</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>ref</code></td>
                  <td>Required for this integration</td>
                  <td>
                    S.MPLE listing record ID. URL-encode it. Echoed in <code>mf:complete</code> for
                    consistency checks; the launching S.MPLE listing record ID is the persistence
                    key on the host. Pass with <code>host_app=smple</code> for per-listing
                    attribution.
                  </td>
                </tr>
                <tr>
                  <td><code>host_app</code></td>
                  <td>Required for this integration</td>
                  <td>
                    Use exactly <code>smple</code>. Together with <code>ref</code>, enables
                    per-listing attribution. Also makes the in-card <em>View furnished layout</em>{' '}
                    control follow <code>return_url</code> (or iframe history) instead of the
                    standalone gallery path.
                  </td>
                </tr>
                <tr>
                  <td><code>return_url</code></td>
                  <td>Optional</td>
                  <td>
                    Snake_case only (<code>return_url</code>, not <code>returnUrl</code>). Used only
                    by the in-card <em>View furnished layout</em> control in the S.MPLE embed. When
                    set, that control navigates to this URL; when omitted, it falls back to iframe
                    history. Not required for upload or persistence. Does not affect{' '}
                    <code>mf:complete</code> or Magic Floor Plan. See{' '}
                    <a href="#view-furnished-layout">View furnished layout</a>.
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="docs-note">
              The upload endpoint does not enforce <code>ref</code> or <code>host_app</code> —
              omitting them still lets <code>mf:complete</code> post, but <code>ref</code> will be
              empty, attribution is lost, and without <code>host_app</code>,{' '}
              <em>View furnished layout</em> uses the standalone gallery path. This integration
              must always send both.
            </div>

            <h3 className="ref-heading">Event payload</h3>
            <DocsCodeBlock text={MF_COMPLETE_PAYLOAD_TEXT} label="mf:complete payload">
              {'{'}
              {'\n  '}
              <span className="k">&quot;type&quot;</span>: <span className="s">&quot;mf:complete&quot;</span>,
              {'\n  '}
              <span className="k">&quot;galleryUrl&quot;</span>: <span className="s">&quot;https://staging-serhant.magicfurnish.com/floor-gallery/507f1f77bcf86cd799439011&quot;</span>,
              {'\n  '}
              <span className="k">&quot;uniqueId&quot;</span>: <span className="s">&quot;f47ac10b-58cc-4372-a567-0e02b2c3d479&quot;</span>,
              {'\n  '}
              <span className="k">&quot;ref&quot;</span>: <span className="s">&quot;YOUR_LISTING_RECORD_ID&quot;</span>
              {'\n}'}
            </DocsCodeBlock>
            <dl className="param-dl">
              <div className="param-row">
                <dt><code>galleryUrl</code></dt>
                <dd>
                  Required. Absolute URL on <code>{SERHANT_ORIGIN}</code>. Store exactly as
                  returned.
                </dd>
              </div>
              <div className="param-row">
                <dt><code>ref</code></dt>
                <dd>
                  Echo of the query <code>ref</code> (S.MPLE listing record ID). Consistency check
                  only.
                </dd>
              </div>
              <div className="param-row">
                <dt><code>uniqueId</code></dt>
                <dd>
                  Opaque MagicFurnish layout id. Do not parse it, and do not assume it equals the{' '}
                  <code>floor-gallery</code> path segment. Persist against the launching S.MPLE
                  listing record ID and <code>galleryUrl</code>.
                </dd>
              </div>
            </dl>

            <h3 className="ref-heading" id="view-furnished-layout">View furnished layout</h3>
            <p>
              In the S.MPLE embed, <em>View furnished layout</em> follows <code>return_url</code>,
              or iframe history when <code>return_url</code> is omitted. It is separate from Magic
              Floor Plan on the listing.
            </p>
            <p>
              Set <code>return_url</code> on the embed URL to continue into a host-owned flow after
              S.MPLE persists <code>galleryUrl</code>. Without <code>return_url</code>, the embed
              control does not open the editor on its own.
            </p>
            <p>
              Outside the embed (standalone uploader), the same control opens the floor gallery and
              may continue into the editor when the user is signed in.
            </p>
          </section>

          <section className="phase docs-phase--ref" id="galleries">
            <h2 className="phase-title">Re-uploads and failures</h2>
            <ul>
              <li>
                Each successful upload creates a new gallery and returns a new{' '}
                <code>galleryUrl</code>. S.MPLE stores that URL privately against the listing.
                It does not go on serhant.com until the agent publishes it.
              </li>
              <li>
                If the uploader&apos;s session saves an edit, MagicFurnish keeps that edit on the
                same listing. A save from a later visit, or from someone else, is not that edit.
                The public site keeps the previously published URL until the agent publishes again.
              </li>
              <li>A failed upload emits no <code>mf:complete</code>.</li>
            </ul>

            <h3 className="ref-heading" id="reconcile">Missed mf:complete</h3>
            <p>
              <code>mf:complete</code> is the fast signal that a gallery is ready. If the host
              closes the embed before that event arrives, S.MPLE can still recover the link by
              asking from its own server. Do not call this from the browser, and do not put the
              key in the iframe.
            </p>
            <p>
              Ask with{' '}
              <code>GET https://api-staging.magicfurnish.com/api/v2/ai/layout-by-ref?ref={'{listing_record_id}'}</code>
              . The embed examples on this page use <code>https://staging-serhant.magicfurnish.com</code>.
              The API key must be scoped to that same host. A key scoped to{' '}
              <code>serhant.magicfurnish.com</code> does not see these uploads, and{' '}
              <code>host_app</code> does not change a key that already has an origin. Production
              still uses the previous guide until this is promoted.
            </p>
            <p>
              Send the <code>X-API-Key</code> and <code>X-API-Secret</code> headers for the key
              MagicFurnish issues to S.MPLE. <code>ref</code> is the same S.MPLE listing record ID
              used on the embed.
            </p>
            <ul>
              <li>
                <code>ready</code> — <code>galleryUrl</code> is the floor plan to hold. It is the
                newest upload, or an edit saved from the uploader&apos;s session, whichever is
                newer.
              </li>
              <li>
                <code>processing</code> — ask again later. Do not publish yet, and do not substitute
                an older plan that was already ready.
              </li>
              <li>
                <code>none</code> — nothing was found for that listing. Do not remove a link that is
                already on serhant.com.
              </li>
            </ul>
            <p>
              Call this again when the agent opens the floor plan to edit, and again on Publish.
              Use the <code>galleryUrl</code> from that response. The first <code>mf:complete</code>{' '}
              is not the address to publish after an edit. An error or a timeout is not{' '}
              <code>none</code>; leave an already published link in place.
            </p>
            <p>
              Hold <code>galleryUrl</code> privately. Write it to Luxury Presence only when the
              agent clicks Publish. See{' '}
              <a href="#publishing">Publishing on serhant.com</a>.
            </p>
          </section>

          <div className="docs-layer-head" role="presentation">
            <span className="docs-layer-kicker">Security &amp; operations</span>
          </div>

          <section className="phase docs-phase--ref" id="security">
            <h2 className="phase-title">Security</h2>
            <ul>
              <li>
                Validate exact <code>event.origin</code> (<code>{SERHANT_ORIGIN}</code>) and{' '}
                <code>event.source</code> (<code>iframe.contentWindow</code>) — required because
                the sender uses <code>targetOrigin: &quot;*&quot;</code>.
              </li>
              <li>
                Uploads are unauthenticated; no browser-side API credential is required for the
                embed.
              </li>
              <li>
                Host CSP: allow <code>{SERHANT_ORIGIN}</code> in <code>frame-src</code>.
              </li>
              <li>
                Stored <code>galleryUrl</code> links are publicly accessible to anyone with the
                URL.
              </li>
              <li>
                MagicFurnish responses are currently servable in any parent frame pending
                SERHANT.&apos;s approved parent origin(s). At launch, <code>frame-ancestors</code>{' '}
                is restricted to those origins.
              </li>
            </ul>
          </section>

          <section className="phase docs-phase--ref" id="troubleshooting">
            <h2 className="phase-title">Troubleshooting</h2>
            <div className="step">
              <div className="troubleshooting">
                <details>
                  <summary>
                    <span className="accordion-title">Blank iframe</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      Confirm that <code>iframe.src</code> has been assigned and that the host
                      Content Security Policy allows <code>{SERHANT_ORIGIN}</code> in{' '}
                      <code>frame-src</code>.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">No <code>mf:complete</code></span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      Confirm that processing finished and a gallery is ready, the listener was
                      registered before <code>iframe.src</code>, the listener is on the iframe&apos;s
                      immediate parent, and the origin and source checks match the example. File
                      upload alone does not emit <code>mf:complete</code>.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">Upload failed</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      Errors stay inside the iframe. No <code>mf:complete</code> is sent.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">Duplicate delivery</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      Persist idempotently on launching S.MPLE listing record ID +{' '}
                      <code>galleryUrl</code>.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">Missed event</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      See <a href="#reconcile">Missed mf:complete</a>.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">Nested iframe</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      <code>mf:complete</code> is delivered to the iframe&apos;s immediate parent.
                      Prefer handling validation and persistence there. If the result must be
                      forwarded to an outer page, the immediate parent must first validate the
                      MagicFurnish message, then forward a host-owned message using a specific
                      target origin. The outer page must validate the forwarding frame&apos;s origin
                      and source.
                    </p>
                  </div>
                </details>
                <details>
                  <summary>
                    <span className="accordion-title">Wrong iframe size</span>
                    <span className="chevron" aria-hidden="true" />
                  </summary>
                  <div className="accordion-body">
                    <p>
                      Keep <code>width: 100%; max-width: 360px; height: 240px;</code> (supported
                      widths 320–360px).
                    </p>
                  </div>
                </details>
              </div>
            </div>
          </section>

          <div className="docs-layer-head" role="presentation">
            <span className="docs-layer-kicker">Additional resources</span>
          </div>

          <section className="phase docs-phase--ref" id="platform">
            <h2 className="phase-title">iOS and smoke test</h2>

            <h3 className="ref-heading">iOS WKWebView</h3>
            <p>
              Make the web view transparent. If the <code>message</code> listener runs outside the
              web view, forward <code>postMessage</code> events to it.
            </p>
            <DocsCodeBlock text={IOS_WEBVIEW_TEXT} label="iOS webview">
              <span className="k">webView</span>.isOpaque = <span className="k">false</span>
              {'\n'}
              <span className="k">webView</span>.backgroundColor = .clear
            </DocsCodeBlock>

            <h3 className="ref-heading">Smoke test</h3>
            <p>
              Use this harness to verify the iframe loads and <code>mf:complete</code> reaches the
              parent. Persistence and host CSP still need a real host page.
            </p>
            <DocsCodePreview
              label="smoke test"
              expanded={smokeExpanded}
              onToggle={() => setSmokeExpanded((open) => !open)}
            >
              <DocsCodeBlock text={TEST_HARNESS_TEXT} label="smoke test" />
            </DocsCodePreview>
          </section>

          <section className="phase docs-phase--ref" id="analytics">
            <h2 className="phase-title">Analytics</h2>
            <ul>
              <li>
                Request partner dashboard access from{' '}
                <a href={`mailto:${DOCS_SUPPORT_EMAIL}`}>{DOCS_SUPPORT_EMAIL}</a>.
              </li>
              <li>
                <a href={`${SERHANT_ORIGIN}/analytics`}>Open Analytics dashboard</a>
              </li>
              <li>
                Per-listing attribution requires both <code>host_app</code> and <code>ref</code> on
                the embed URL.
              </li>
            </ul>
          </section>

          <div className="docs-layer-head" role="presentation">
            <span className="docs-layer-kicker">Production launch</span>
          </div>

          <section className="confirm" id="publishing">
            <h2 className="confirm-title">Publishing on serhant.com</h2>
            <p className="confirm-lead">
              The S.MPLE embed is complete once <code>galleryUrl</code> is stored. That link stays
              private until the agent clicks Publish. That click writes it into the Luxury Presence
              field used by the serhant.com listing template.
            </p>
            <ul className="checklist">
              <li>
                Identify the Luxury Presence external URL field used by the serhant.com listing
                template.
              </li>
              <li>
                On Publish, call the lookup and write that <code>galleryUrl</code> into the field.
                Do not write the first <code>mf:complete</code> after an edit, and do not write the
                field again until the agent publishes the next edit.
              </li>
              <li>
                Open Edit on that same <code>galleryUrl</code>. Do not clear the field when the
                lookup returns <code>none</code>, an error, or a timeout.
              </li>
              <li>
                Show Magic Floor Plan on serhant.com when the field has a value.
              </li>
            </ul>
            <DocsFigure
              src={integrationJourney}
              aspectRatio="16 / 9"
              alt="Four steps: upload in MagicFurnish, store the URL privately in S.MPLE, publish when the agent is ready, and show Magic Floor Plan on serhant.com."
              caption="From upload to Magic Floor Plan on serhant.com."
            />
          </section>

          <section className="confirm" id="kickoff">
            <h2 className="confirm-title">Launch inputs</h2>
            <p className="confirm-lead">
              Coordinate these with MagicFurnish before go-live. They are not required to build the
              iframe itself.
            </p>
            <ol>
              <li>Approved parent origin(s) that will embed the uploader.</li>
              <li>
                Luxury Presence external URL field for the serhant.com listing template.
              </li>
              <li>
                S.MPLE writes that field when the agent clicks Publish.
              </li>
            </ol>
          </section>

          <footer className="foot">
            <div className="foot-row">
              <div className="foot-meta">
                <span>
                  Questions? <a href={`mailto:${DOCS_SUPPORT_EMAIL}`}>{DOCS_SUPPORT_EMAIL}</a>
                </span>
                <span className="foot-updated">Last updated September 28, 2026</span>
              </div>
              <img src={wordmarkSrc} alt="" className="docs-foot-wordmark" aria-hidden="true" />
            </div>
            <nav className="foot-legal" aria-label="Legal">
              <a href={MF_TERMS_URL} target="_blank" rel="noopener noreferrer">
                Terms
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <a href={MF_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <a href={MF_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                Support
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <span className="foot-version">{APP_VERSION}</span>
            </nav>
          </footer>

        </div>
      </div>
    </div>
  );
};

export default IntegrationGuideDocs;
